import { useState, useCallback, memo } from "react";
import { Modal, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import { getCroppedImg } from "../../utils/getCroppedImg";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import { User } from "../../types/Types";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../../Context/AppContext";
import { useTranslation } from "react-i18next";

export type UploadImageVariant = "banner" | "profile";

interface UploadImageProps {
  showBannerModal: boolean;
  setShowBannerModal: (show: boolean) => void;
  userData?: User;
  /** Banner (3:1) or circular profile photo (1:1). Default banner. */
  variant?: UploadImageVariant;
}

const MODAL_BODY_STYLE = { minHeight: 400 };

const CROPPER_CONTAINER_STYLE = {
  position: "relative" as const,
  width: "100%",
  height: 400,
  background: "#333",
};

const SKIP_FILE_KEYS = new Set([
  "bannerImage",
  "BannerImage",
  "professionalPhoto",
  "ProfessionalPhoto",
  "personalProfessionalPhoto",
  "PersonalProfessionalPhoto",
  "businessLogo",
  "BusinessLogo",
  "idDocument",
  "IdDocument",
]);

const UploadImage = memo(function UploadImage({
  showBannerModal,
  setShowBannerModal,
  userData,
  variant = "banner",
}: UploadImageProps) {
  const { t } = useTranslation();
  const isProfile = variant === "profile";
  const aspectRatio = isProfile ? 1 : 3 / 1;
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const { objectId } = useAppContext();
  const queryClient = useQueryClient();

  const handleClose = useCallback(() => {
    setImageSrc(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowBannerModal(false);
  }, [setShowBannerModal]);

  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      handleClose();
    },
  });

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, "blob");
      const fileName = isProfile ? "profile.jpg" : "banner.jpg";
      const file = new File([blob], fileName, { type: "image/jpeg" });

      const formData = new FormData();
      if (isProfile) {
        formData.append("professionalPhoto", file);
      } else {
        formData.append("bannerImage", file);
      }

      Object.entries(userData || {}).forEach(([key, value]) => {
        if (SKIP_FILE_KEYS.has(key) || value === null || value === undefined) {
          return;
        }
        if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      });

      updateUser(formData);
    } catch (error) {
      console.error(
        isProfile ? "Failed to upload profile photo:" : "Failed to upload banner:",
        error,
      );
    }
  }, [imageSrc, croppedAreaPixels, userData, updateUser, isProfile]);

  return (
    <Modal centered show={showBannerModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isProfile
            ? t("account.uploadProfilePhoto")
            : t("account.uploadBanner")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={MODAL_BODY_STYLE}>
        {!imageSrc ? (
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={onFileChange}
            aria-label={
              isProfile
                ? t("account.uploadProfilePhoto")
                : t("account.uploadBanner")
            }
          />
        ) : (
          <div style={CROPPER_CONTAINER_STYLE}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              cropShape={isProfile ? "round" : "rect"}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={handleClose}>
          {t("account.cancel")}
        </Button>
        {imageSrc && (
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={isPending}
          >
            {isPending ? t("account.saving") : t("account.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
});

UploadImage.displayName = "UploadImage";

export default UploadImage;
